import React from 'react';
import { FormattedMessage } from 'react-intl';
import noop from 'lodash/fp/noop';

import DeviceLogsTable from '~/features/deviceLogs/components/DeviceLogsTable';
import OtherDevicesLogRequest from '~/features/deviceLogs/components/logRequest/OtherDevicesLogRequest';
import SetOtherDevicesLogLevel from '~/features/deviceLogs/components/logLevel/SetOtherDevicesLogLevel';
import CM4LogsTable from '~/features/deviceLogs/components/logsTable/CM4LogsTable';
import { CM4 } from '~/features/devices/constants/deviceTypes';
import { verifyCM4DeviceOffline } from '~/features/base/utils/timeUtils';
import { env } from '~/env';
import CM4SetDeviceLogLevel from '~/features/deviceLogs/components/logLevel/CM4SetDeviceLogLevel';
import { LogsSearchCriteriaDeviceTypeEnum } from '~/api/models/logs-search-criteria';
import CM4LogRequestWrapper from '~/features/deviceLogs/components/logRequest/CM4LogRequestWrapper';
import moment from "moment";
import { ExpanderPanel } from '@rio-cloud/rio-uikit';
import {useEulaBlockedQuery} from "~/api/vehicles/VehicleManagement.api";

interface DeviceLogsProps {
    serialNumber: string;
    controlDevice: {
        originalSerialNumber: string;
        type: string;
        lastCheckForUpdateTimestamp?: number;
    };
    controlDeviceInfo?: {
        installedDeliverables?: any;
    };
    canCreateDeviceActions?: boolean;
    canEditDeviceActions?: boolean;
    isDeviceLogsUser?: boolean;
    deviceLog?: any[];
    showDeviceActionEditor?: () => void;
    downloadDeviceLogFile?: (serialNumber: string, logFile: any) => void;
}

const DeviceLogs: React.FC<DeviceLogsProps> = ({
                                                   controlDevice,
                                                   controlDeviceInfo,
                                                   canCreateDeviceActions = false,
                                                   canEditDeviceActions = false,
                                                   isDeviceLogsUser = false,
                                                   deviceLog = [],
                                                   downloadDeviceLogFile = noop,
                                               }) => {
    const onDownloadLogFile = (logFile: any) => {
        downloadDeviceLogFile(controlDevice.originalSerialNumber, logFile);
    };
    const { data: eulaStatus, isLoading: isEulaStatusLoading } = useEulaBlockedQuery(
        controlDevice?.vin,
    );

    const isCM4Offline = env.isStagingEnv
        ? false
        : verifyCM4DeviceOffline(
            controlDeviceInfo?.lastCheckForUpdateTimestamp,
            controlDeviceInfo?.installedDeliverables
        );

    const logRequestComponents: Record<string, React.ReactNode> = {
        [CM4]: (
            <CM4LogRequestWrapper
                serialNumberList={[controlDevice.originalSerialNumber]}
                isDeviceLogsUser={isDeviceLogsUser}
                requestDisabled={isCM4Offline}
            />
        ),
        default: (
            <OtherDevicesLogRequest
                serialNumber={controlDevice.originalSerialNumber}
                canCreateDeviceActions={canCreateDeviceActions}
                canEditDeviceActions={canEditDeviceActions}
            />
        ),
    };

    const logTableComponents: Record<string, React.ReactNode> = {
        [CM4]: (
            <CM4LogsTable
                serialNumber={controlDevice.originalSerialNumber}
                deviceType={controlDevice.type as LogsSearchCriteriaDeviceTypeEnum}
            />
        ),
        default: (
            <>
                <div className="h5 margin-bottom-20">
          <span className="h5">
            <FormattedMessage id="intl-msg:logs" />
          </span>
                </div>
                <DeviceLogsTable
                    serialNumber={controlDevice.originalSerialNumber}
                    deviceLog={deviceLog}
                    onDownloadLogFile={onDownloadLogFile}
                />
            </>
        ),
    };

    // @ts-ignore
    const setLogLevelComponents: Record<string, React.ReactNode> = {
        [CM4]: (
            <CM4SetDeviceLogLevel
                serialNumber={controlDevice.originalSerialNumber}
                isDeviceLogsUser={isDeviceLogsUser}
                lastEssentialImageReported={controlDeviceInfo?.lastEssentialImageReported}
            />
        ),
        default: (
            <SetOtherDevicesLogLevel
                serialNumber={controlDevice.originalSerialNumber}
                deviceType={controlDevice.type as LogsSearchCriteriaDeviceTypeEnum}
                isDeviceLogsUser={isDeviceLogsUser}
                canCreateDeviceActions={canCreateDeviceActions}
            />
        ),
    };

    const logRequestComponent = logRequestComponents[controlDevice.type] || logRequestComponents.default;
    const logTableComponent = logTableComponents[controlDevice.type] || logTableComponents.default;
    const setLogLevelComponent = setLogLevelComponents[controlDevice.type] || setLogLevelComponents.default;

    return (
        <div style={{ backgroundColor: '#F5F5F5' }} className="padding-bottom-10">
            {isEulaStatusLoading && (
                <div className="padding-10">
                    <div className="text-center">
                        <i className="fa fa-spinner fa-spin" />
                    </div>
                </div>
            )}
            {!isEulaStatusLoading && eulaStatus?.isEulaBlocked && (
                <div className="padding-10">
                    <div >
                        <div className="alert alert-warning display-flex align-items-center">
                            <i className="rioglyph rioglyph-warning-sign margin-right-5" />
                            <FormattedMessage id='intl-msg:logs.eulaDataActWarning' values=
                                {{ date: moment(eulaStatus?.eulaBlockedDate).format(
                                        'YYYY-MM-DD') }}/>
                        </div>
                    </div>
                </div>

            )}

            {!isEulaStatusLoading && !eulaStatus?.isEulaBlocked &&
                <div className="display-flex flex-column">
                <div className="rounded-large bg-white margin-top-10 margin-left-10 margin-right-10">
                    {setLogLevelComponent}
                </div>
                <div className="rounded-large bg-white margin-top-10 margin-left-10 margin-right-10">
                    <ExpanderPanel open
                                   title={<span className={'h5'}><FormattedMessage
                                       id='intl-msg:createLogRequest'/></span>}
                                   bsStyle='blank'>
                        {logRequestComponent}
                    </ExpanderPanel>
                </div>
            </div>}
            <div className="rounded-large bg-white margin-top-10 margin-left-10 margin-right-10 padding-20">
                {logTableComponent}
            </div>
        </div>
    );
};

export default DeviceLogs;
